import React, { useState, useEffect } from "react";
import AxiosInstance from "../components/Axios";
import {
  Box,
  TextField,
  Button,
  Typography,
  Stack,
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Chip,
  Card,
  CardContent,
  Divider,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  styled,
} from "@mui/material";
import {
  Add,
  Delete,
  Edit,
  ExpandMore,
  BorderColor,
  SaveAlt,
  Cancel,
  Visibility,
  SettingsOutlined,
} from "@mui/icons-material";
import Swal from "sweetalert2";
import colors from "../components/constants/Colors";

// Styles for table cells and rows
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  "&.MuiTableCell-head": {
    backgroundColor: colors.primary,
    color: theme.palette.common.white,
    fontWeight: "normal",
  },
  "&.MuiTableCell-body": {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: "#f5f5f5",
  },
}));

const VariableParamManager = () => {
  const [variables, setVariables] = useState([]);
  const [name, setName] = useState("");
  const [values, setValues] = useState([{ value: "" }]);
  const [selectedVariable, setSelectedVariable] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openValuesDialog, setOpenValuesDialog] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [expandForm, setExpandForm] = useState(true);

  // Get project ID from localStorage
  const project_id = localStorage.getItem("project_id");

  useEffect(() => {
    fetchVariables();
  }, [project_id]);

  const fetchVariables = async () => {
    setLoadingData(true);
    try {
      const res = await AxiosInstance.get(
        `/projects/${project_id}/param-variables/`
      );
      setVariables(res.data);
    } catch (err) {
      console.error("Error fetching variables:", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Unable to load variables",
        backdrop: `rgba(0,0,0,0.4)`,
        allowOutsideClick: false,
        position: "center",
        willOpen: () => {
          // Appliquer un z-index élevé directement
          setTimeout(() => {
            const container = document.querySelector(".swal2-container");
            if (container) {
              container.style.zIndex = 2000;
            }
          }, 100);
        },
      });
    } finally {
      setLoadingData(false);
    }
  };

  const handleAddValue = () => {
    setValues([...values, { value: "" }]);
  };

  const handleChangeValue = (index, newValue) => {
    const updated = [...values];
    updated[index].value = newValue;
    setValues(updated);
  };

  const handleRemoveValue = (index) => {
    if (values.length === 1) {
      Swal.fire({
        icon: "warning",
        title: "Warning",
        text: "You must keep at least one value",
      });
      return;
    }
    const updated = [...values];
    updated.splice(index, 1);
    setValues(updated);
  };

  const resetForm = () => {
    setName("");
    setValues([{ value: "" }]);
    setSelectedVariable(null);
  };

  const handleSubmit = async () => {
    // Validation
    if (!name.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Required Field",
        text: "Variable name is required",
      });
      return;
    }

    const filteredValues = values.filter((v) => v.value.trim() !== "");
    if (filteredValues.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Missing Values",
        text: "At least one value is required",
      });
      return;
    }

    try {
      if (selectedVariable) {
        // Update existing variable
        const res = await AxiosInstance.put(
          `/projects/${project_id}/param-variables/${selectedVariable.id}/`,
          {
            name,
            values: filteredValues,
          }
        );

        setVariables(
          variables.map((v) => (v.id === selectedVariable.id ? res.data : v))
        );

        Swal.fire({
          icon: "success",
          title: "Update Successful",
          text: "The variable has been updated successfully",
        });
      } else {
        // Check if variable with same name already exists
        const variableExists = variables.some(
          (v) => v.name.toLowerCase() === name.toLowerCase()
        );
        if (variableExists) {
          Swal.fire({
            icon: "error",
            title: "Duplicate Variable",
            text: `A variable with the name "${name}" already exists.`,
            backdrop: `rgba(0,0,0,0.4)`,
            allowOutsideClick: false,
            position: "center",
            willOpen: () => {
              // Appliquer un z-index élevé directement
              setTimeout(() => {
                const container = document.querySelector(".swal2-container");
                if (container) {
                  container.style.zIndex = 2000;
                }
              }, 100);
            },
          });
          return;
        }

        // Create new variable
        const res = await AxiosInstance.post(
          `/projects/${project_id}/param-variables/`,
          {
            name,
            values: filteredValues,
          }
        );

        setVariables([...variables, res.data]);

        Swal.fire({
          icon: "success",
          title: "Creation Successful",
          text: "The variable has been created successfully",
          backdrop: `rgba(0,0,0,0.4)`,
          allowOutsideClick: false,
          position: "center",
          willOpen: () => {
            // Appliquer un z-index élevé directement
            setTimeout(() => {
              const container = document.querySelector(".swal2-container");
              if (container) {
                container.style.zIndex = 2000;
              }
            }, 100);
          },
        });
      }

      resetForm();
    } catch (err) {
      console.error("Error:", err);

      // Check for specific error responses
      if (err.response) {
        // Check if the error is about duplicate variable
        if (err.response.data && err.response.data.name) {
          const nameErrors = err.response.data.name;
          if (Array.isArray(nameErrors) && nameErrors.length > 0) {
            // Check if error contains duplicate message
            if (
              nameErrors.some(
                (msg) =>
                  msg.toLowerCase().includes("already exists") ||
                  msg.toLowerCase().includes("unique") ||
                  msg.toLowerCase().includes("duplicate")
              )
            ) {
              Swal.fire({
                icon: "error",
                title: "Duplicate Variable",
                text: `A variable with the name "${name}" already exists.`,
                position: "center",
                willOpen: () => {
                  // Appliquer un z-index élevé directement
                  setTimeout(() => {
                    const container =
                      document.querySelector(".swal2-container");
                    if (container) {
                      container.style.zIndex = 2000;
                    }
                  }, 100);
                },
              });
              return;
            }
          }
        }

        // Display the error message from the server if available
        Swal.fire({
          icon: "error",
          title: "Error",
          text:
            err.response.data.error ||
            err.response.data.detail ||
            (err.response.data.name
              ? err.response.data.name[0]
              : "An error occurred"),
          position: "center",
          willOpen: () => {
            // Appliquer un z-index élevé directement
            setTimeout(() => {
              const container = document.querySelector(".swal2-container");
              if (container) {
                container.style.zIndex = 2000;
              }
            }, 100);
          },
        });
      } else {
        // Fallback error message
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "An unexpected error occurred. Please try again.",
          position: "center",
          willOpen: () => {
            // Appliquer un z-index élevé directement
            setTimeout(() => {
              const container = document.querySelector(".swal2-container");
              if (container) {
                container.style.zIndex = 2000;
              }
            }, 100);
          },
        });
      }
    }
  };

  const handleEdit = (variable) => {
    setName(variable.name);
    setValues(variable.values.map((v) => ({ value: v.value })));
    setSelectedVariable(variable);
    setExpandForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleViewValues = (variable) => {
    setSelectedVariable(variable);
    setOpenValuesDialog(true);
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: colors.primary,
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      backdrop: `rgba(0,0,0,0.4)`,
      allowOutsideClick: false,
      position: "center",
      willOpen: () => {
        // Appliquer un z-index élevé directement
        setTimeout(() => {
          const container = document.querySelector(".swal2-container");
          if (container) {
            container.style.zIndex = 2000;
          }
        }, 100);
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await AxiosInstance.delete(
            `/projects/${project_id}/param-variables/${id}/`
          );
          setVariables(variables.filter((v) => v.id !== id));

          Swal.fire({
            icon: "success",
            title: "Deleted!",
            text: "The variable has been deleted",
            backdrop: `rgba(0,0,0,0.4)`,
            allowOutsideClick: false,
            position: "center",
            willOpen: () => {
              // Appliquer un z-index élevé directement
              setTimeout(() => {
                const container = document.querySelector(".swal2-container");
                if (container) {
                  container.style.zIndex = 2000;
                }
              }, 100);
            },
          });
        } catch (err) {
          console.error("Error during deletion:", err);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Deletion failed",
            backdrop: `rgba(0,0,0,0.4)`,
            allowOutsideClick: false,
            position: "center",
            willOpen: () => {
              // Appliquer un z-index élevé directement
              setTimeout(() => {
                const container = document.querySelector(".swal2-container");
                if (container) {
                  container.style.zIndex = 2000;
                }
              }, 100);
            },
          });
        }
      }
    });
  };

  return (
    <Box sx={{ padding: 0, width: 800 }}>
      <Accordion
        expanded={expandForm}
        onChange={() => setExpandForm(!expandForm)}
      >
        <AccordionSummary
          expandIcon={<ExpandMore style={{ color: colors.primary }} />}
          aria-controls="form-content"
          id="form-header"
        >
          <Typography variant="h6" fontWeight={400} color={colors.secondary}>
            <SettingsOutlined />{" "}
            {selectedVariable ? "Edit Variable" : "Variables configurations"}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Card elevation={1}>
            <CardContent>
              <Stack spacing={3}>
                <TextField
                  label="Variable Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  fullWidth
                  variant="outlined"
                  placeholder="Enter variable name"
                  size="small"
                />
                <Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight={500}
                    ></Typography>
                    <Button
                      variant="outlined"
                      startIcon={<Add />}
                      onClick={handleAddValue}
                      size="small"
                      sx={{
                        color: colors.secondary,
                        borderColor: colors.secondary,
                      }}
                    >
                      Add Value
                    </Button>
                  </Box>

                  <Paper
                    elevation={1}
                    sx={{ p: 2, backgroundColor: "#f9f9f9" }}
                  >
                    <Stack spacing={2}>
                      {values.map((v, idx) => (
                        <Box
                          key={idx}
                          display="flex"
                          alignItems="center"
                          gap={1}
                        >
                          <TextField
                            label={`Value ${idx + 1}`}
                            value={v.value}
                            onChange={(e) =>
                              handleChangeValue(idx, e.target.value)
                            }
                            fullWidth
                            variant="outlined"
                            size="small"
                          />
                          <IconButton
                            onClick={() => handleRemoveValue(idx)}
                            color="error"
                            size="small"
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                </Box>

                <Box display="flex" justifyContent="flex-end" gap={2}>
                  <Button
                    variant="outlined"
                    onClick={resetForm}
                    startIcon={<Cancel />}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    startIcon={<SaveAlt />}
                    sx={{ backgroundColor: colors.primary }}
                  >
                    {selectedVariable ? "Update" : "Create"}
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Box mt={4}>
            {loadingData ? (
              <Typography>Loading data...</Typography>
            ) : variables.length === 0 ? (
              <TableContainer component={Paper} elevation={3}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Variable Name</StyledTableCell>
                      <StyledTableCell>Values</StyledTableCell>
                      <StyledTableCell align="center">Actions</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <StyledTableCell
                        colSpan={4}
                        align="center"
                        sx={{ py: 3 }}
                      >
                        <Typography variant="subtitle1" color="text.secondary">
                          There is no variables
                        </Typography>
                      </StyledTableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <TableContainer component={Paper} elevation={3}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Variable Name</StyledTableCell>
                      <StyledTableCell>Values</StyledTableCell>
                      <StyledTableCell align="center">Actions</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {variables.map((v) => (
                      <StyledTableRow key={v.id}>
                        <StyledTableCell>{v.name}</StyledTableCell>
                        <StyledTableCell>
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                          >
                            {v.values.slice(0, 3).map((val, idx) => (
                              <Chip key={idx} label={val.value} size="small" />
                            ))}
                            {v.values.length > 3 && (
                              <Chip
                                label={`+${v.values.length - 3}`}
                                size="small"
                                color="primary"
                                onClick={() => handleViewValues(v)}
                                sx={{ cursor: "pointer" }}
                              />
                            )}
                          </Box>
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          <Box>
                            {/* <IconButton 
                    color="primary" 
                    onClick={() => handleViewValues(v)}
                    title="View values"
                  >
                    <Visibility />
                  </IconButton> */}
                            <IconButton
                              color="secondary"
                              onClick={() => handleEdit(v)}
                              title="Edit"
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              color="error"
                              onClick={() => handleDelete(v.id)}
                              title="Delete"
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Dialog to display all values */}
      <Dialog
        open={openValuesDialog}
        onClose={() => setOpenValuesDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Values for: {selectedVariable?.name}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={1}>
            {selectedVariable?.values.map((val, idx) => (
              <Grid item xs={6} sm={4} md={3} key={idx}>
                <Chip
                  label={val.value}
                  variant="outlined"
                  sx={{ width: "100%", justifyContent: "flex-start" }}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenValuesDialog(false)}>Close</Button>
          <Button
            color="secondary"
            onClick={() => {
              setOpenValuesDialog(false);
              handleEdit(selectedVariable);
            }}
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VariableParamManager;
